import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';
import CertificateFormBase, { validateGasCertificate } from './CertificateFormBase';
import { pbEngineer } from '../../lib/pb';
import { CERT_TYPE_LABELS } from '../../data/mappers';
import './GasCertForm.css';

// Import step components
import PropertyStep from './steps/PropertyStep';
import EngineerStep from './steps/EngineerStep';
import ApplianceStep from './steps/ApplianceStep';
import ClassificationStep from './steps/ClassificationStep';
import GWNFaultStep from './steps/GWNFaultStep';
import SignatureCapture from './steps/SignatureCapture';

// Simple summary step (will be expanded)
function SummaryStep({ 
  form, 
  classification, 
  photos, 
  certificateType,
  onEditStep,
  onSave,
  isSaving,
  hasSignature,
  allowUnsigned = false
}) {
  const certLabel = CERT_TYPE_LABELS[certificateType] || certificateType;
  const canSubmit = allowUnsigned || hasSignature;

  return (
    <div className="summary-step">
      <div className="panel summary-panel">
        <h3>Certificate Summary</h3>
        <p className="field-hint">Review before submitting</p>
      </div>

      <div className="summary-section">
        <h4>Certificate Type</h4>
        <p>{certLabel}</p>
      </div>

      <div className="summary-section">
        <h4>Property</h4>
        <p>{form.property_address || 'Not specified'}</p>
        {form.landlord_name && (
          <p className="summary-detail">Landlord: {form.landlord_name}</p>
        )}
      </div>

      <div className="summary-section">
        <h4>Engineer</h4>
        <p>{form.engineer_name || 'Not specified'}</p>
        {form.engineer_registration_number && (
          <p className="summary-detail">Gas Safe: {form.engineer_registration_number}</p>
        )}
      </div>

      {classification && (
        <div className="summary-section">
          <h4>Safety Classification</h4>
          <p className={`classification-badge classification-${classification}`}>
            {classification.replace(/_/g, ' ').toUpperCase()}
          </p>
        </div>
      )}

      {form.appliances && form.appliances.length > 0 && (
        <div className="summary-section">
          <h4>Appliances ({form.appliances.length})</h4>
          <ul className="summary-list">
            {form.appliances.map((app, idx) => (
              <li key={idx}>
                <span>{app.type || 'Unspecified'} - {app.make || ''} {app.model || ''}</span>
                <span className={`safe-badge safe-${app.safe_to_use}`}>
                  {app.safe_to_use?.toUpperCase() || 'NOT SET'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {form.faults && form.faults.length > 0 && (
        <div className="summary-section">
          <h4>Faults ({form.faults.length})</h4>
          <ul className="summary-list">
            {form.faults.map((fault, idx) => (
              <li key={idx}>
                <span>{fault.location || 'Unspecified'}</span>
                <span className={`classification-badge classification-${fault.classification}`}>
                  {fault.classification?.replace(/_/g, ' ').toUpperCase()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {photos && photos.length > 0 && (
        <div className="summary-section">
          <h4>Photos ({photos.length})</h4>
          <div className="photo-summary">
            <SafeIcon icon={Icons.Camera} size={16} />
            <span>{photos.length} photo(s) captured</span>
          </div>
        </div>
      )}

      <div className="summary-section">
        <h4>Signature Status</h4>
        {hasSignature ? (
          <p className="signature-status signed">
            <SafeIcon icon={Icons.Check} size={16} />
            Signed
          </p>
        ) : (
          <p className="signature-status unsigned">
            <SafeIcon icon={Icons.AlertCircle} size={16} />
            Not yet signed
          </p>
        )}
      </div>

      <button
        type="button"
        className="primary-button submit-certificate-button"
        onClick={onSave}
        disabled={isSaving || !canSubmit}
        aria-label="Submit certificate"
      >
        {isSaving ? (
          <>Saving...</>
        ) : (
          <>
            <SafeIcon icon={Icons.Check} size={18} />
            {allowUnsigned ? 'Save Draft Certificate' : 'Submit Certificate'}
          </>
        )}
      </button>

      {!hasSignature && !allowUnsigned && (
        <p className="field-hint warning-hint">
          <SafeIcon icon={Icons.AlertTriangle} size={14} />
          Signature required before submission
        </p>
      )}
      {!hasSignature && allowUnsigned && (
        <p className="field-hint">
          No signature attached — saved as a draft. The engineer can sign before issue.
        </p>
      )}
    </div>
  );
}

// Step rendering map
function renderStepContent(stepId, formState) {
  switch (stepId) {
    case 'property':
      return <PropertyStep {...formState} />;
    case 'engineer':
      return <EngineerStep {...formState} />;
    case 'appliances':
      return <ApplianceStep {...formState} />;
    case 'classification':
      return <ClassificationStep {...formState} />;
    case 'faults':
      return <GWNFaultStep {...formState} />;
    case 'summary':
      return <SummaryStep {...formState} />;
    default:
      return <div>Step not implemented: {stepId}</div>;
  }
}

// Map a validation message to the section(s) that own it, so desktop
// review can badge/jump without the user stepping through every section.
function errorToStepId(message, steps) {
  const ids = new Set(steps.map((s) => s.id));
  const m = String(message || '').toLowerCase();
  const candidates = [];
  if (/property|landlord|address/.test(m)) candidates.push('property');
  if (/engineer|gas safe|business/.test(m)) candidates.push('engineer');
  if (/appliance|flue/.test(m)) candidates.push('appliances');
  if (/classification|safe to use|immediately dangerous|at risk|not to current/.test(m)) candidates.push('classification');
  if (/fault/.test(m)) candidates.push('faults', 'fault');
  if (/inspection/.test(m)) candidates.push('inspection');
  if (/service item/.test(m)) candidates.push('service');
  if (/work carried out|diagnosis|parts/.test(m)) candidates.push('diagnosis', 'fault', 'parts');
  if (/test/.test(m)) candidates.push('tests', 'system');
  return candidates.find((id) => ids.has(id)) || null;
}

// Main Certificate Wizard Component
export default function CertificateWizard({
  certificateType,
  jobId,
  client,
  engineerJob,
  priorYearCertificate,
  allowUnsigned = false,
  redirectTo = '/engineer/certificates',
  onSave,
  onCancel
}) {
  const { data, add } = useData();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [signatureOverlay, setSignatureOverlay] = useState(false);
  const [signatureData, setSignatureData] = useState(null);

  // Use the base form hook
  const formState = CertificateFormBase({
    certificateType,
    jobId,
    client,
    initialData: priorYearCertificate?.form_data || {},
    onSave: async (certificateData) => {
      try {
        // Signature must persist as a real engineer_signatures record and
        // attach via the engineer_signature_id relation (save-path fix,
        // handoff §2 step 3) — not a dropped payload field.
        if (signatureData) {
          const engineerRecord = pbEngineer.authStore?.record;
          const sig = await pbEngineer.collection('engineer_signatures').create({
            tenant_id: engineerRecord?.tenant_id,
            client_id: certificateData.client_id,
            engineer_job_id: certificateData.job_id || null,
            client_name: client?.name || '',
            signature_data: signatureData,
            signed_at: new Date().toISOString().split('T')[0],
          });
          certificateData.engineer_signature_id = sig.id;
        }

        // Unsigned drafts (office on behalf of engineer) stay draft status.
        if (allowUnsigned && !signatureData) {
          certificateData.status = 'draft';
        }
        
        // Save via DataProvider
        await add('certificates', certificateData);
        
        notify('Certificate created successfully', 'success');
        onSave?.(certificateData);
        if (redirectTo) navigate(redirectTo);
        return true;
      } catch (error) {
        notify(error.message || 'Failed to create certificate', 'error');
        return false;
      }
    },
    onCancel
  });

  // CertificateFormBase always returns a form state object — the guard
  // below was dead code, but it sat before hooks and tripped the
  // rules-of-hooks check. Removed; hooks stay unconditional.

  const {
    step,
    form,
    classification,
    photos,
    validationErrors,
    isSaving,
    autosaving,
    steps,
    isMobile,
    setField,
    setNestedField,
    setClassification,
    nextStep,
    prevStep,
    goToStep,
    addItem,
    removeItem,
    handlePhotoCapture,
    handleSignature,
    handleSave,
    setValidationErrors,
    data: dataContext
  } = formState;

  const currentStep = steps[step];
  const canGoNext = step < steps.length - 1;
  const canGoBack = step > 0;
  const isLastStep = step === steps.length - 1;

  // Desktop is tabs, not a linear wizard: run the FULL validator on every
  // render so issues in any section are visible without stepping through
  // each one. Mobile keeps per-step validation on Next (constrained flow).
  const allErrors = useMemo(
    () => (isMobile ? [] : validateGasCertificate(certificateType, form, classification)),
    [isMobile, certificateType, form, classification],
  );
  const errorsByStep = useMemo(() => {
    const map = {};
    for (const err of allErrors) {
      const id = errorToStepId(err, steps);
      if (id) (map[id] = map[id] || []).push(err);
    }
    return map;
  }, [allErrors, steps]);

  // Don't show the cross-section review panel until the user has actually
  // interacted (an untouched blank form isn't "wrong" yet) or a step/submit
  // validation has already fired.
  const [dirty, setDirty] = useState(false);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setDirty(true);
  }, [form, classification]);
  const showReview = !isMobile && (validationErrors.length > 0 || (dirty && allErrors.length > 0));

  // Signature handling
  const handleSignatureSave = (dataUrl) => {
    setSignatureData(dataUrl);
    handleSignature(dataUrl);
    setSignatureOverlay(false);
    notify('Signature captured', 'success');
  };

  const handleSignatureCancel = () => {
    setSignatureOverlay(false);
  };

  // Photo capture handling
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handlePhotoCapture(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="certificate-wizard">
      {/* Progress Indicator - mobile only */}
      {isMobile && (
        <div className="cert-progress">
          <div className="cert-progress-header">
            <span className="eyebrow">{CERT_TYPE_LABELS[certificateType]}</span>
            <span className="step-indicator">
              Step {step + 1} of {steps.length}
            </span>
          </div>
          <div className="cert-progress-bar">
            <div 
              className="cert-progress-fill" 
              style={{ width: `${((step + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Desktop section navigation — freely navigable (tabs, not linear).
          Any section can be clicked directly; sections with issues carry an
          error badge so office staff see problems without stepping through. */}
      {!isMobile && (
        <div className="cert-section-nav">
          <div className="panel nav-panel">
            <h4>Certificate Sections</h4>
            <ul className="step-nav-list">
              {steps.map((stepInfo, idx) => {
                const sectionErrors = errorsByStep[stepInfo.id] || [];
                return (
                  <li key={idx}>
                    <button
                      type="button"
                      className={`step-nav-item ${idx === step ? 'active' : ''} ${idx < step ? 'completed' : ''} ${sectionErrors.length ? 'has-errors' : ''}`}
                      onClick={() => goToStep(idx)}
                      aria-label={`Go to ${stepInfo.title}${sectionErrors.length ? ` (${sectionErrors.length} issue${sectionErrors.length > 1 ? 's' : ''})` : ''}`}
                    >
                      <span className="step-number">{idx + 1}</span>
                      <span className="step-title">{stepInfo.title}</span>
                      {sectionErrors.length > 0 && (
                        <span className="step-error-count">{sectionErrors.length}</span>
                      )}
                      {idx < step && !sectionErrors.length && (
                        <SafeIcon icon={Icons.Check} size={14} />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className="cert-step-content">
        {/* Cross-section review panel (desktop only) — all current issues
            visible at once, with jump-to-section, regardless of the active
            section. This is the tabs-review behaviour office staff need. */}
        {showReview && (
          <div className="cert-review-issues" role="alert">
            <h4>Review issues before submitting</h4>
            <ul>
              {allErrors.map((err, i) => {
                const targetId = errorToStepId(err, steps);
                const targetIdx = targetId ? steps.findIndex((s) => s.id === targetId) : -1;
                return (
                  <li key={i}>
                    <span>{err}</span>
                    {targetIdx >= 0 && (
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => goToStep(targetIdx)}
                      >
                        Fix in {steps[targetIdx].title}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {/* Step title */}
        <div className="step-header">
          <h2>{currentStep?.title}</h2>
          <p className="field-hint">{currentStep?.description}</p>
        </div>

        {/* Form content */}
        <form className="cert-form" onSubmit={(e) => {
          e.preventDefault();
          if (isLastStep) {
            handleSave();
          } else {
            nextStep();
          }
        }}>
          {/* Current step content */}
          {renderStepContent(currentStep?.id, {
            ...formState,
            allowUnsigned,
            errors: validationErrors
          })}

          {/* Photo capture area - always available */}
          <div className="photo-capture-section">
            <label>
              <span className="eyebrow">Add Photos</span>
              <span className="photo-capture-label">
                Capture photos of installation, defects, or compliance evidence
              </span>
            </label>
            <div className="photo-capture-actions">
              <label className="secondary-button photo-upload-button">
                <SafeIcon icon={Icons.Camera} size={18} />
                <span>Capture Photo</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoUpload}
                  className="hidden-file-input"
                />
              </label>
              {photos.length > 0 && (
                <span className="photo-count">
                  {photos.length} photo(s) added
                </span>
              )}
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="cert-nav">
            {canGoBack && (
              <button
                type="button"
                className="secondary-button"
                onClick={prevStep}
                disabled={isSaving}
                aria-label="Go to previous step"
              >
                <SafeIcon icon={Icons.ArrowLeft} size={18} />
                Back
              </button>
            )}

            {/* Signature button for last step */}
            {isLastStep && !signatureData && (
              <button
                type="button"
                className="primary-button signature-button"
                onClick={() => setSignatureOverlay(true)}
                disabled={isSaving}
                aria-label="Capture signature"
              >
                <SafeIcon icon={Icons.Pen} size={18} />
                {isMobile ? 'Sign' : 'Add Signature'}
              </button>
            )}

            {canGoNext && (
              <button
                type="submit"
                className="primary-button"
                disabled={isSaving}
                aria-label="Go to next step"
              >
                Next
                <SafeIcon icon={Icons.ArrowRight} size={18} />
              </button>
            )}
          </div>

          {/* Autosave indicator */}
          {autosaving && (
            <div className="cert-autosave">
              <SafeIcon icon={Icons.Spin} size={14} />
              <span>Auto-saving...</span>
            </div>
          )}
        </form>
      </div>

      {/* Signature overlay (full screen on mobile) */}
      {signatureOverlay && (
        <div className="signature-overlay">
          <SignatureCapture
            onSave={handleSignatureSave}
            onCancel={handleSignatureCancel}
            isMobile={isMobile}
          />
        </div>
      )}
    </div>
  );
}